import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { type Router } from 'vue-router';

import { backOff } from 'exponential-backoff';

import { useAuth } from '@/composables/Auth.ts';
import { useAuthStore } from '@/stores/AuthStore.ts';

const retryMap = new Map<string, boolean>();

const handle401Error = async (
  axios: AxiosInstance,
  originalConfig: InternalAxiosRequestConfig,
  error: AxiosError,
) => {
  const authStore = useAuthStore();
  const { logout } = useAuth();
  const configId = originalConfig.url + (originalConfig.method || 'get');

  try {
    const token = await authStore.getAccessTokenFromRefreshToken();
    if (!token) return logout();

    const originalRequest = error.config ?? originalConfig;
    originalRequest.headers['Authorization'] = `Bearer ${token}`;

    // Awaited so the finally cleanup runs only after the retry settles
    // until then, a recursive 401 still sees hasRetried=true and avoids looping.
    return await axios(originalRequest);
  } finally {
    retryMap.delete(configId);
  }
};

export const handleError = async (
  axios: AxiosInstance,
  router: Router,
  originalConfig: InternalAxiosRequestConfig,
  error: AxiosError,
) => {
  const { logout } = useAuth();

  const status = error.response?.status;

  if (status === undefined) {
    return Promise.reject(error);
  }

  if (status >= 500) {
    try {
      await backOff(
        async () => {
          const response = await fetch(`${import.meta.env.VITE_API_URL}/api/health`);
          if (response.status !== 200) {
            throw new Error('Health check failed');
          }
        },
        {
          delayFirstAttempt: true,
          numOfAttempts: 4,
          startingDelay: 500,
          timeMultiple: 2.71,
        },
      );
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      router.push('/service-down');
    }

    return Promise.reject(error);
  }

  const configId = originalConfig.url + (originalConfig.method || 'get');
  const hasRetried = retryMap.get(configId);
  const isRefreshRequest =
    typeof originalConfig.url === 'string' && originalConfig.url.endsWith('/auth/refresh');

  if (status === 401 && isRefreshRequest) {
    logout();
    return Promise.reject(error);
  }

  if (status === 401 && !hasRetried) {
    retryMap.set(configId, true);

    return handle401Error(axios, originalConfig, error);
  } else if (status === 401 && hasRetried) {
    retryMap.delete(configId);

    logout();
    return Promise.reject(error);
  }

  if (status === 404) {
    return Promise.reject(error);
  }

  return Promise.reject(error);
};
